import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";

const heroImg = "/hero-v2.png";

/* ── Fonts ── */
const fontLink = document.createElement("link");
fontLink.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=DM+Sans:wght@300;400;500&display=swap";
fontLink.rel = "stylesheet";
if (!document.head.querySelector('link[href*="Playfair"]')) document.head.appendChild(fontLink);

const styleEl = document.createElement("style");
styleEl.textContent = `
  @keyframes fadeIn { from{opacity:0}to{opacity:1} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)} }
  @keyframes scaleIn { from{opacity:0;transform:scale(0.96)}to{opacity:1;transform:scale(1)} }
  * { box-sizing:border-box; margin:0; padding:0; }
  html,body { width:100%; overflow-x:hidden; }
  ::selection { background:rgba(168,85,247,0.15); }
  ::-webkit-scrollbar { width:3px; }
  ::-webkit-scrollbar-thumb { background:rgba(168,85,247,0.25); border-radius:10px; }
  .btn-main:hover { opacity:0.88; transform:translateY(-1px); }
  .btn-ghost:hover { background:rgba(168,85,247,0.07) !important; }
  .rev-card:hover { border-color:rgba(168,85,247,0.3) !important; }
`;
if (!document.head.querySelector("style[data-bln3]")) {
  styleEl.setAttribute("data-bln3","1");
  document.head.appendChild(styleEl);
}

function useBreakpoint() {
  const [w, setW] = useState(typeof window!=="undefined"?window.innerWidth:1200);
  useEffect(()=>{
    const h=()=>setW(window.innerWidth);
    window.addEventListener("resize",h);
    return ()=>window.removeEventListener("resize",h);
  },[]);
  return { isMobile:w<640, isTablet:w>=640&&w<1024, isDesktop:w>=1024 };
}

function useInView(t=0.1){
  const ref=useRef(null);
  const [v,setV]=useState(false);
  useEffect(()=>{
    const o=new IntersectionObserver(([e])=>{if(e.isIntersecting)setV(true)},{threshold:t});
    if(ref.current)o.observe(ref.current);
    return ()=>o.disconnect();
  },[t]);
  return [ref,v];
}

function Reveal({children,delay=0,style={}}){
  const [ref,v]=useInView();
  return(
    <div ref={ref} style={{
      opacity:v?1:0,
      transform:v?"translateY(0)":"translateY(22px)",
      transition:`opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      ...style,
    }}>{children}</div>
  );
}

const LogoImg = ({size=28})=>(
  <img src="/logo-v1.png"
    alt="Bellason" width={size} height={size}
    style={{display:"block",objectFit:"contain"}}/>
);

const Arrow = ({size=13,color="currentColor"})=>(
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

const Star = ({size=13,filled=true})=>(
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled?"#c084fc":"none"} stroke="#c084fc" strokeWidth="1.5">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const XIcon = ({size=13})=>(
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const MenuIcon = ({size=17})=>(
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="17" x2="21" y2="17"/>
  </svg>
);

const CheckIcon = ({size=13})=>(
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

export default function Landing(){
  const navigate = useNavigate();
  const {theme, toggleTheme} = useTheme();
  const [menu, setMenu] = useState(false);
  const [modal, setModal] = useState(false);
  const {isMobile, isDesktop} = useBreakpoint();

  const goReg = (role)=>navigate(`/registro?rol=${role}`);
  const FP = "'Playfair Display', Georgia, serif";
  const FB = "'DM Sans', system-ui, sans-serif";

  const reviews = [
    {initial:"J",name:"Janet E.",zone:"Villa Satélite",service:"Uñas",text:"En 10 minutos tenía 3 ofertas. Elegí la mejor y llegó puntual. Facilísimo.",hue:270},
    {initial:"O",name:"Olivia A.",zone:"Perisur",service:"Maquillaje",text:"Ver el precio antes de decidir fue clave. Sin sorpresas, sin regateo.",hue:310},
    {initial:"P",name:"Perla M.",zone:"San Benito",service:"Profesional",text:"El lead gratis me dio mi primera clienta. Ya recuperé lo invertido.",hue:340},
  ];

  return(
    <div style={{minHeight:"100vh",background:"var(--bln-bg)",color:"var(--bln-text)",fontFamily:FB,overflowX:"hidden"}}>

      {/* NAV */}
      <nav style={{
        position:"sticky",top:0,zIndex:100,
        display:"flex",alignItems:"center",justifyContent:"space-between",
        padding: isMobile?"14px 20px":"16px 52px",
        background:"var(--bln-bg)",
        borderBottom:"1px solid var(--bln-accent-tint)",
        backdropFilter:"blur(20px)",
        animation:"fadeIn 0.5s ease both",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:"9px"}}>
          <LogoImg size={isMobile?22:28}/>
          <span style={{fontSize:isMobile?"15px":"19px",fontWeight:300,letterSpacing:"1px",fontFamily:FB}}>bellason</span>
        </div>

        <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
          {/* dark mode */}
          <button onClick={toggleTheme} aria-label="tema" style={{
            width:"34px",height:"34px",borderRadius:"8px",
            background:"transparent",border:"1px solid var(--bln-accent-tint)",
            color:"var(--bln-text)",cursor:"pointer",fontSize:"13px",
            display:"flex",alignItems:"center",justifyContent:"center",
          }}>{theme==="dark"?"☀️":"🌙"}</button>

          {/* iniciar sesión — solo desktop */}
          {isDesktop && (
            <button onClick={()=>navigate("/login")} className="btn-ghost" style={{
              padding:"8px 18px",borderRadius:"8px",
              background:"transparent",border:"1px solid var(--bln-accent-tint)",
              color:"var(--bln-text-muted)",fontSize:"13px",cursor:"pointer",fontFamily:FB,
              transition:"background 0.2s",
            }}>Iniciar sesión</button>
          )}

          {/* registrarme */}
          <button onClick={()=>setModal(true)} className="btn-main" style={{
            padding:isMobile?"8px 14px":"9px 20px",borderRadius:"8px",
            background:"var(--bln-accent)",border:"none",
            color:"#fff",fontSize:"13px",cursor:"pointer",fontFamily:FB,fontWeight:500,
            display:"flex",alignItems:"center",gap:"6px",
            transition:"opacity 0.2s,transform 0.2s",
          }}>
            Registrarme <Arrow size={12} color="#fff"/>
          </button>

          {/* mobile menu */}
          {!isDesktop && (
            <button onClick={()=>setMenu(!menu)} style={{
              width:"34px",height:"34px",borderRadius:"8px",
              background:"transparent",border:"1px solid var(--bln-accent-tint)",
              color:"var(--bln-text)",cursor:"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",
            }}>
              {menu?<XIcon size={14}/>:<MenuIcon size={16}/>}
            </button>
          )}
        </div>
      </nav>

      {/* mobile drawer */}
      {menu && !isDesktop && (
        <div style={{
          background:"var(--bln-bg)",borderBottom:"1px solid var(--bln-accent-tint)",
          padding:"8px 20px 16px",animation:"fadeIn 0.2s ease both",
        }}>
          <button onClick={()=>{navigate("/login");setMenu(false);}} style={{
            width:"100%",padding:"11px",marginTop:"8px",
            background:"transparent",border:"1px solid var(--bln-accent-tint)",
            borderRadius:"8px",color:"var(--bln-text)",cursor:"pointer",fontSize:"14px",fontFamily:FB,
          }}>Iniciar sesión</button>
        </div>
      )}

      {/* ═══ HERO ═══ */}
      <section style={{
        position:"relative",
        minHeight: isDesktop?"calc(100vh - 61px)":isMobile?"auto":"560px",
        overflow:"hidden",
        display:"flex",
        flexDirection: isDesktop?"row":"column",
      }}>
        {/* imagen — full right en desktop, full top en mobile */}
        <div style={{
          position: isDesktop?"absolute":"relative",
          right:0, top:0, bottom:0,
          width: isDesktop?"58%":"100%",
          height: isDesktop?"100%":isMobile?"72vw":"480px",
          overflow:"hidden",
          flexShrink:0,
        }}>
          <img src={heroImg} alt="Profesional Bellason" style={{
            width:"100%", height:"100%",
            objectFit:"cover",
            objectPosition: isMobile?"center 15%":"center top",
            animation:"fadeIn 0.9s ease both",
          }}/>
          {/* fade izquierdo — solo desktop */}
          {isDesktop && (
            <div style={{
              position:"absolute",top:0,left:0,bottom:0,width:"45%",
              background:"linear-gradient(to right, var(--bln-bg) 0%, transparent 100%)",
              pointerEvents:"none",
            }}/>
          )}
          {/* fade abajo — mobile */}
          {!isDesktop && (
            <div style={{
              position:"absolute",bottom:0,left:0,right:0,height:"5%",
              background:"linear-gradient(to bottom, transparent 0%, var(--bln-bg) 100%)",
              pointerEvents:"none",
            }}/>
          )}
        </div>

        {/* texto hero */}
        <div style={{
          position: isDesktop?"relative":"relative",
          zIndex:2,
          display:"flex",flexDirection:"column",
          justifyContent:"center",
          padding: isMobile?"32px 24px 48px":isDesktop?"0 0 0 60px":"40px 48px",
          maxWidth: isDesktop?"52%":"100%",
          width: isDesktop?"52%":"100%",
          alignItems: isDesktop?"flex-start":"center",
          textAlign: isDesktop?"left":"center",
        }}>
       
          {/* título */}
          <h1 style={{
            fontFamily:FP,
            fontSize:isMobile?"25px":isDesktop?"68px":"54px",
            fontWeight:400,lineHeight:1.05,
            color:"var(--bln-text)",
            marginBottom:"20px",
            animation:"fadeUp 0.6s ease 0.2s both",
            letterSpacing:"-0.5px",
          }}>
            Solicita tu servicio de <em style={{color:"var(--bln-accent)",fontStyle:"italic"}}>belleza</em> a domicilio hoy
          </h1>

          <p style={{
            fontSize:isMobile?"14px":"15px",
            color:"var(--bln-text-muted)",lineHeight:1.75,
            maxWidth:"380px",marginBottom:"32px",
            fontFamily:FB,fontWeight:300,
            animation:"fadeUp 0.6s ease 0.3s both",
          }}>
            Publica gratis, recibe ofertas de profesionales verificadas y elige la mejor. Solo pagas cuando alguien te convenza.
          </p>

          {/* CTAs */}
          <div style={{
            display:"flex",gap:"10px",
            flexDirection:isMobile?"column":"row",
            width:isMobile?"100%":"auto",
            marginBottom:"36px",
            animation:"fadeUp 0.6s ease 0.4s both",
          }}>
            <button onClick={()=>goReg("client")} className="btn-main" style={{
              padding:"13px 28px",borderRadius:"8px",
              background:"var(--bln-accent)",border:"none",
              color:"#fff",fontSize:"14px",cursor:"pointer",fontFamily:FB,fontWeight:500,
              display:"flex",alignItems:"center",justifyContent:"center",gap:"7px",
              transition:"opacity 0.2s,transform 0.2s",
              width:isMobile?"100%":"auto",
            }}>
              Solicitar servicio <Arrow size={13} color="#fff"/>
            </button>
            <button onClick={()=>goReg("provider")} className="btn-ghost" style={{
              padding:"13px 28px",borderRadius:"8px",
              background:"transparent",border:"1px solid var(--bln-accent-tint)",
              color:"var(--bln-text-muted)",fontSize:"14px",cursor:"pointer",fontFamily:FB,fontWeight:300,
              transition:"background 0.2s",
              width:isMobile?"100%":"auto",
            }}>
              Soy profesional
            </button>
          </div>

          {/* social proof */}
          <div style={{
            display:"flex",alignItems:"center",gap:"12px",
            justifyContent:isDesktop?"flex-start":"center",
            animation:"fadeUp 0.6s ease 0.5s both",
          }}>
            <div style={{display:"flex"}}>
              {[44,68,65,17].map((n,i)=>(
                <img key={n} src={`https://randomuser.me/api/portraits/women/${n}.jpg`} alt=""
                  style={{width:"30px",height:"30px",borderRadius:"50%",
                    border:"2px solid var(--bln-bg)",marginLeft:i===0?0:"-9px",objectFit:"cover"}}/>
              ))}
            </div>
            <div>
              <div style={{display:"flex",gap:"2px",marginBottom:"2px"}}>
                {[0,1,2,3,4].map(i=><Star key={i} size={11}/>)}
              </div>
              <span style={{fontSize:"11px",color:"var(--bln-text-muted)",fontFamily:FB}}>+10K usuarias en Hermosillo</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <div style={{
        display:"grid",
        gridTemplateColumns:"repeat(3,1fr)",
        borderTop:"1px solid var(--bln-accent-tint)",
        borderBottom:"1px solid var(--bln-accent-tint)",
      }}>
        {[
          {num:"2K+",label:"Profesionales"},
          {num:"4.9",label:"Calificación"},
          {num:"20K+",label:"Servicios"},
        ].map((s,i)=>(
          <Reveal key={s.label} delay={i*0.08}>
            <div style={{
              padding:isMobile?"18px 8px":"22px 16px",
              textAlign:"center",
              borderRight:i<2?"1px solid var(--bln-accent-tint)":"none",
            }}>
              <div style={{fontSize:isMobile?"22px":"28px",fontFamily:FP,fontWeight:400,color:"var(--bln-accent)",lineHeight:1}}>{s.num}</div>
              <div style={{fontSize:"11px",color:"var(--bln-text-muted)",fontFamily:FB,marginTop:"5px",letterSpacing:"0.3px"}}>{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* ═══ RESEÑAS + CALIFICACIONES ═══ */}
      <section style={{padding:isMobile?"56px 20px":"80px 52px",borderBottom:"1px solid var(--bln-accent-tint)"}}>
        <Reveal>
          <div style={{textAlign:"center",marginBottom:isMobile?"36px":"52px"}}>
            <p style={{fontSize:"11px",letterSpacing:"2.5px",textTransform:"uppercase",color:"var(--bln-accent)",fontFamily:FB,marginBottom:"12px"}}>Reseñas</p>
            <h2 style={{fontFamily:FP,fontSize:isMobile?"30px":"40px",fontWeight:400,color:"var(--bln-text)",lineHeight:1.1}}>
              Lo que dicen nuestras<br/><em style={{color:"var(--bln-accent)"}}>usuarias</em>
            </h2>
          </div>
        </Reveal>

        {/* calificación global centrada */}
        <Reveal delay={0.1}>
          <div style={{
            display:"flex",flexDirection:"column",alignItems:"center",
            marginBottom:isMobile?"36px":"52px",
          }}>
            <div style={{
              fontSize:isMobile?"56px":"72px",fontFamily:FP,fontWeight:400,
              color:"var(--bln-accent)",lineHeight:1,marginBottom:"8px",
            }}>4.9</div>
            <div style={{display:"flex",gap:"4px",marginBottom:"8px"}}>
              {[0,1,2,3,4].map(i=><Star key={i} size={16}/>)}
            </div>
            <span style={{fontSize:"13px",color:"var(--bln-text-muted)",fontFamily:FB}}>Promedio de +500 reseñas verificadas</span>
          </div>
        </Reveal>

        {/* tarjetas de reseñas */}
        <div style={{
          display:"grid",
          gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",
          gap:"14px",
          maxWidth:"900px",margin:"0 auto",
        }}>
          {reviews.map((r,i)=>(
            <Reveal key={r.name} delay={i*0.1}>
              <div className="rev-card" style={{
                padding:"22px",borderRadius:"12px",
                border:"1px solid var(--bln-accent-tint)",
                transition:"border-color 0.2s",
                height:"100%",
              }}>
                <div style={{display:"flex",gap:"2px",marginBottom:"14px"}}>
                  {[0,1,2,3,4].map(j=><Star key={j} size={11}/>)}
                </div>
                <p style={{
                  fontSize:"13px",color:"var(--bln-text-muted)",lineHeight:1.8,
                  fontFamily:FB,fontWeight:300,fontStyle:"italic",marginBottom:"18px",
                }}>"{r.text}"</p>
                <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                  <div style={{
                    width:"32px",height:"32px",borderRadius:"50%",flexShrink:0,
                    background:`hsl(${r.hue},45%,38%)`,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:"12px",fontWeight:500,color:"#fff",fontFamily:FB,
                  }}>{r.initial}</div>
                  <div>
                    <div style={{fontSize:"12px",fontWeight:500,color:"var(--bln-text)",fontFamily:FB}}>{r.name}</div>
                    <div style={{fontSize:"11px",color:"var(--bln-text-muted)",fontFamily:FB}}>{r.zone} · {r.service}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ CTA FINAL ═══ */}
      <section style={{
        padding:isMobile?"72px 24px":"100px 52px",
        textAlign:"center",
        borderBottom:"1px solid var(--bln-accent-tint)",
      }}>
        <Reveal>
          <div style={{maxWidth:"480px",margin:"0 auto"}}>
            <LogoImg size={36}/>
            <h2 style={{
              fontFamily:FP,
              fontSize:isMobile?"36px":"52px",
              fontWeight:400,lineHeight:1.1,
              color:"var(--bln-text)",margin:"20px 0 14px",
            }}>
              ¿Lista para tu primer<br/><em style={{color:"var(--bln-accent)"}}>servicio?</em>
            </h2>
            <p style={{fontSize:"14px",color:"var(--bln-text-muted)",fontFamily:FB,fontWeight:300,lineHeight:1.8,marginBottom:"36px"}}>
              Publicar es gratis. Sin tarjeta. Sin compromisos.
            </p>
            <div style={{
              display:"flex",gap:"10px",justifyContent:"center",
              flexDirection:isMobile?"column":"row",
            }}>
              <button onClick={()=>goReg("client")} className="btn-main" style={{
                padding:"13px 28px",borderRadius:"8px",
                background:"var(--bln-accent)",border:"none",
                color:"#fff",fontSize:"14px",cursor:"pointer",fontFamily:FB,fontWeight:500,
                display:"flex",alignItems:"center",justifyContent:"center",gap:"7px",
                transition:"opacity 0.2s,transform 0.2s",
                width:isMobile?"100%":"auto",
              }}>
                Quiero un servicio <Arrow size={13} color="#fff"/>
              </button>
              <button onClick={()=>goReg("provider")} className="btn-ghost" style={{
                padding:"13px 28px",borderRadius:"8px",
                background:"transparent",border:"1px solid var(--bln-accent-tint)",
                color:"var(--bln-text-muted)",fontSize:"14px",cursor:"pointer",fontFamily:FB,fontWeight:300,
                transition:"background 0.2s",
                width:isMobile?"100%":"auto",
              }}>
                Soy profesional
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{
        padding:isMobile?"18px 20px":"20px 52px",
        display:"flex",alignItems:"center",
        justifyContent:isMobile?"center":"space-between",
        flexDirection:isMobile?"column":"row",
        gap:"8px",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
          <LogoImg size={16}/>
          <span style={{fontSize:"12px",color:"var(--bln-text-muted)",fontFamily:FB,fontWeight:300,letterSpacing:"0.5px"}}>bellason</span>
          <span style={{fontSize:"11px",color:"var(--bln-text-muted)",opacity:0.4,fontFamily:FB}}>· Hermosillo, Sonora</span>
        </div>
        <span style={{fontSize:"11px",color:"var(--bln-text-muted)",opacity:0.4,fontFamily:FB}}>bellason.mx · Todos los derechos reservados</span>
      </footer>

      {modal && <RoleModal onSelect={goReg} onClose={()=>setModal(false)}/>}
    </div>
  );
}

/* ═══ MODAL ═══ */
function RoleModal({onSelect,onClose}){
  const [role,setRole]=useState(null);
  const {isMobile}=useBreakpoint();
  const FP="'Playfair Display', Georgia, serif";
  const FB="'DM Sans', system-ui, sans-serif";

  return(
    <div style={{
      position:"fixed",inset:0,zIndex:200,
      background:"rgba(0,0,0,0.65)",backdropFilter:"blur(10px)",
      display:"flex",alignItems:"center",justifyContent:"center",
      padding:"16px",animation:"fadeIn 0.2s ease both",
    }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"var(--bln-bg)",border:"1px solid var(--bln-accent-tint)",
        borderRadius:"14px",
        padding:isMobile?"26px 18px":"36px 32px",
        width:"100%",maxWidth:"400px",
        position:"relative",animation:"scaleIn 0.22s ease both",
      }}>
        <button onClick={onClose} style={{
          position:"absolute",top:"12px",right:"12px",
          width:"26px",height:"26px",borderRadius:"50%",
          background:"transparent",border:"1px solid var(--bln-accent-tint)",
          color:"var(--bln-text-muted)",cursor:"pointer",
          display:"flex",alignItems:"center",justifyContent:"center",
        }}><XIcon size={12}/></button>

        <div style={{textAlign:"center",marginBottom:"24px"}}>
          <h2 style={{fontFamily:FP,fontSize:"24px",fontWeight:400,color:"var(--bln-text)",marginBottom:"6px"}}>
            Únete a <em style={{color:"var(--bln-accent)"}}>Bellason</em>
          </h2>
          <p style={{fontSize:"13px",color:"var(--bln-text-muted)",fontFamily:FB}}>¿Cómo quieres usar la plataforma?</p>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"14px"}}>
          {[
            {key:"client",title:"Soy cliente",desc:"Quiero reservar servicios"},
            {key:"provider",title:"Soy profesional",desc:"Ofrezco servicios"},
          ].map(({key,title,desc})=>(
            <button key={key} onClick={()=>setRole(key)} style={{
              padding:"18px 12px",borderRadius:"10px",cursor:"pointer",
              background:role===key?"rgba(168,85,247,0.08)":"transparent",
              border:`1px solid ${role===key?"var(--bln-accent)":"var(--bln-accent-tint)"}`,
              color:"var(--bln-text)",textAlign:"center",
              transition:"background 0.2s,border-color 0.2s",
            }}>
              <div style={{fontSize:"14px",fontFamily:FB,fontWeight:500,marginBottom:"4px"}}>{title}</div>
              <div style={{fontSize:"12px",color:"var(--bln-text-muted)",fontFamily:FB,fontWeight:300}}>{desc}</div>
            </button>
          ))}
        </div>

        {role && (
          <div style={{
            padding:"12px 14px",borderRadius:"8px",
            border:"1px solid var(--bln-accent-tint)",
            marginBottom:"12px",
            animation:"fadeUp 0.2s ease both",
          }}>
            {(role==="provider"
              ?["1 lead gratis para empezar","$35 MXN solo si te eligen","Perfil con fotos y reseñas"]
              :["Publicar es completamente gratis","Recibe varias ofertas y elige","Sin tarjeta ni compromisos"]
            ).map(p=>(
              <div key={p} style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"6px"}}>
                <CheckIcon size={12}/>
                <span style={{fontSize:"12px",color:"var(--bln-text-muted)",fontFamily:FB}}>{p}</span>
              </div>
            ))}
          </div>
        )}

        <button disabled={!role} onClick={()=>role&&onSelect(role)} style={{
          width:"100%",padding:"13px",borderRadius:"8px",border:"none",
          background:role?"var(--bln-accent)":"var(--bln-accent-tint)",
          color:role?"#fff":"var(--bln-text-muted)",
          fontSize:"14px",fontFamily:FB,fontWeight:500,
          cursor:role?"pointer":"not-allowed",
          display:"flex",alignItems:"center",justifyContent:"center",gap:"7px",
          transition:"opacity 0.2s",marginBottom:"10px",
        }}>
          {role?`Continuar como ${role==="client"?"cliente":"profesional"}`:"Selecciona tu perfil"}
        </button>

        <p style={{textAlign:"center",fontSize:"12px",color:"var(--bln-text-muted)",fontFamily:FB}}>
          ¿Ya tienes cuenta?{" "}
          <a href="/login" style={{color:"var(--bln-accent)",textDecoration:"none"}}>Iniciar sesión</a>
        </p>
      </div>
    </div>
  );
}